import React, { cloneElement, isValidElement, useEffect, useRef, useState } from "react"

const parseDistance = distance => {
  if (!distance) {
    return "20px"
  }

  return typeof distance === "number" ? `${distance}px` : distance
}

const getTransform = ({
  bottom,
  left,
  right,
  top,
  direction,
  distance,
  visible,
}) => {
  if (visible) {
    return "translate3d(0, 0, 0)"
  }

  const offset = parseDistance(distance)

  if (direction === "up" || bottom) {
    return `translate3d(0, ${offset}, 0)`
  }

  if (direction === "down" || top) {
    return `translate3d(0, -${offset}, 0)`
  }

  if (direction === "left" || right) {
    return `translate3d(${offset}, 0, 0)`
  }

  if (direction === "right" || left) {
    return `translate3d(-${offset}, 0, 0)`
  }

  return `translate3d(0, ${offset}, 0)`
}

const Fade = ({
  children,
  distance,
  duration = 500,
  delay = 0,
  bottom,
  left,
  right,
  top,
  direction,
  ...rest
}) => {
  const containerRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const childCount = React.Children.count(children)
  const isDomChild =
    childCount === 1 && isValidElement(children) && typeof children.type === "string"

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const node = containerRef.current

    if (!(node instanceof Element)) {
      return undefined
    }

    if (!("IntersectionObserver" in window)) {
      setVisible(true)
      return undefined
    }

    const observer = new window.IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisible(true)
            observer.disconnect()
          }
        })
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -10% 0px",
      }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  const animationStyle = {
    opacity: visible ? 1 : 0,
    transform: getTransform({
      bottom,
      left,
      right,
      top,
      direction,
      distance,
      visible,
    }),
    transitionProperty: "opacity, transform",
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
    transitionTimingFunction: "ease",
    willChange: "opacity, transform",
  }

  if (isDomChild) {
    const mergedClassName = [children.props.className, "react-reveal", rest.className]
      .filter(Boolean)
      .join(" ")

    return cloneElement(children, {
      ...rest,
      ref: containerRef,
      className: mergedClassName,
      style: {
        ...children.props.style,
        ...animationStyle,
        ...rest.style,
      },
    })
  }

  return (
    <div
      {...rest}
      ref={containerRef}
      className={["react-reveal", rest.className].filter(Boolean).join(" ")}
      style={{
        ...animationStyle,
        ...rest.style,
      }}
    >
      {children}
    </div>
  )
}

export default Fade
